/**
 * Allgemeine Utility-Methoden im Zusammenhang mit base.metamodel.json / Model,
 * welche für 3D-Modelle benutzt werden
 */

import { v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import { Urls } from "oc-commons/Urls";
import { UniversalLoader } from "oc-threejs/loader/UniversalLoader";
import { ReplacingMaterialFactory } from "oc-threejs/loader/ReplacingMaterialFactory";
import * as Commons from "oc-commons/Commons";
import { Connector } from "oc-interaction/connectors/Connector";
import * as Textures from "../oc-threejs/Textures";
import { IModel, IModelData } from "./Models";
import _ from "lodash";
import { Box3, Matrix4, Mesh, Object3D, Quaternion, Vector3 } from "three";

export class ModelsHelper {
  /**
   * Extrahier Rotation, Scale, Position aus modelData
   * @param modelData
   * @returns {{quaternion: THREE.Quaternion, scale: Vector3, position: Vector3}}
   */
  getQuaternionScalePosition(modelData: IModelData | undefined): {
    quaternion: Quaternion;
    scale: Vector3;
    position: Vector3;
  } {
    const quaternion = new Quaternion();
    if (modelData?.Rotation) {
      quaternion.copy(modelData.Rotation);
    }

    const scale = v3(1, 1, 1);
    if (modelData?.Scale) {
      scale.copy(modelData.Scale);
    }

    const position = v3();
    if (modelData?.Position) {
      position.copy(modelData.Position);
    }
    return { quaternion, scale, position };
  }

  /**
   * Extrahiert ein Matrix entsprechend der Standard-Transformationen aus modelData
   * @param model
   * @returns {THREE.Matrix4}
   */
  getMatrix(model: IModel): Matrix4 {
    const modelData = model.ModelData ?? {};

    const matrix = new Matrix4();
    const h = new Matrix4();

    const { quaternion, scale, position } = this.getQuaternionScalePosition(modelData);
    matrix.multiply(h.makeScale(scale.x, scale.y, scale.z));
    matrix.multiply(h.makeRotationFromQuaternion(quaternion));

    if (modelData.AutoCenter && modelData.boundingBoxUntransformed) {
      const boundingBox = modelData.boundingBoxUntransformed.clone();
      const centerToOrigin = (boundingBox as any).grid(v3(0.5, 0.5, 0.5)); // Box3 extension 'grid'
      matrix.multiply(h.makeTranslation(-centerToOrigin.x, -centerToOrigin.y, -centerToOrigin.z));
      boundingBox.applyMatrix4(matrix);
      const gridPosition = modelData.GridPosition || v3(0.5, 0.5, 0.5);
      const fixPoint = (boundingBox as any).grid(gridPosition);
      matrix.multiply(h.makeTranslation(-fixPoint.x, -fixPoint.y, -fixPoint.z));
    }

    matrix.multiply(h.makeTranslation(position.x, position.y, position.z));
    return matrix;
  }

  /**
   * Gibt die boundingBox basierend auf der transformierten ModelData.boundingBoxUntransformed zurück.
   *
   * Früher wurde in ModelData sowohl boundingBox als auch boundingBoxUntransformed gespeichert.
   * Problem daran war, dass die boundingBox sich durch Änderungen an position, quaternion oder scale
   * ändert, und aktualisiert werden muss. Das musste aber manuell erfolgen, weil ein garantiertes automatisches
   * Tracken der Änderung im Backend und neuberechnen der boundingbox sehr aufwändig gewesen wäre.
   * Daher ist der neue Ansatz, auf das persistieren der boundingBox zu verzichten, und nur noch die
   * boundingBoxUntransformed zu persisiteren. Die boundingBox wird über den ModelsHelper zur Laufzeit
   * aus der boundingBoxUntransformed berechnet.
   *
   * @param model
   */
  getBoundingBox(model: IModel): Box3 {
    if (model?.ModelData == null || !model.ModelData.boundingBoxUntransformed) {
      return new Box3();
    }
    const box = model.ModelData.boundingBoxUntransformed.clone();
    const matrix = this.getMatrix(model);
    return box.applyMatrix4(matrix);
  }

  /**
   * Wendet die Model-Paramter Rotation, Scale, AutoCenter und Position auf ein 3D-Objekt an
   */
  applyToObject3d(model: IModel, object3d: Object3D): Object3D {
    const matrix = this.getMatrix(model);
    // Ensure ModelData exists and default init if needed? getMatrix handles empty logic.
    // Logic says: copyMatrix to object3d.
    // Also model.ModelData.boundingBox = ...
    // If ModelData is missing, getMatrix returns identity.
    // But logic accesses model.ModelData.boundingBoxUntransformed below.
    // I should check if ModelData exists or if getMatrix implied it.
    // Original JS: const box = model.ModelData.boundingBoxUntransformed.clone(); -> crashes if ModelData null
    // So logic assumes ModelData is present and valid if applyToObject3d is called?
    // Or at least boundingBoxUntransformed exists.
    if (!model.ModelData || !model.ModelData.boundingBoxUntransformed) {
      // Fallback or let it crash? Preservation of logic implies letting it crash or assuming valid input.
      // But TS strictness needs handling. I will allow it to crash if I must cast, or return/throw.
      // I will assume it exists as per original code.
      throw new Error("ModelData or boundingBoxUntransformed missing");
    }

    const box = model.ModelData.boundingBoxUntransformed.clone();
    (object3d as any).copyMatrix(matrix); // Object3D extension? copyMatrix?
    // ThreeJS Object3D doesn't have copyMatrix. It has transformation properties.
    // It implies extension.
    (object3d as any).boundingBox = model.ModelData.boundingBox = box.applyMatrix4(matrix);
    if (STAGE.settings.shadows.enabled) {
      Scenes.filter(object3d, (o: any) => o instanceof Mesh).forEach((o: any) => {
        o.receiveShadow = true;
        o.castShadow = true;
      });
    }
    return object3d;
  }

  getConnectorsFromModelRelativeToObject(model: IModel, object3d: any): Connector[] {
    const connectors: Connector[] = [];
    if (model.Connector && Array.isArray(model.Connector)) {
      model.Connector.forEach((config: any) => {
        const connector = new Connector([
          config.connectorname,
          config.connectortype,
          config.strengthFactor,
        ]);
        const connectorOptions = {
          position: v3(object3d.posbox.grid(config.position_relativ)).add(config.position_absolut),
          quaternion: config.rotation,
        };
        connectors.push(connector);
        connector.setConnectorOptions(connectorOptions);
      });
    }
    return connectors;
  }

  /**
   * @returns {Promise}
   */
  loadModelPromise(
    model: IModel,
    materialMap: Record<string, any> | undefined = undefined,
  ): Promise<Object3D> {
    if (!model.Path) throw new Error("Model Path missing");
    const url = Urls.isAbsolute(model.Path)
      ? model.Path
      : APP.settings.wkfgContentBaseUrl + model.Path;
    const map = Object.assign({}, materialMap, model.MaterialMap);
    const materialFactory = new ReplacingMaterialFactory(
      map,
      UniversalLoader.getMaterialFactory() as any,
    );
    const cacheKey = Commons.cacheKey(model.Path, map);
    return UniversalLoader.loadModelPromise(url, materialFactory, cacheKey).then(
      (object: Object3D) => {
        // applyToObject3d requires ModelData.
        if (!model.ModelData) {
          // If loaded model doesn't have data on 'model' object yet...
          // Logic failure if applyToObject3d crashes?
          // But loadModelPromise usage implies model already has metadata?
          // Or maybe UniversalLoader fills something? No.
          // I'll assume model has metadata.
        }
        return this.applyToObject3d(model, object);
      },
    );
  }

  setModelDataFromObject(model: IModel, object3d: Object3D): void {
    if (!model.ModelData) {
      model.ModelData = {};
    }
    model.ModelData.materialNames = Textures.getMaterials(object3d).map(
      (m: any) => m.userData.originalName ?? m.name,
    );
    model.ModelData.objectNames = _.uniq(Scenes.map(object3d, (o: any) => o.name)).filter(
      (x: any) => !Commons.isEmpty(x),
    );
    const o2 = object3d.clone();
    o2.position.set(0, 0, 0);
    o2.quaternion.set(0, 0, 0, 1);
    o2.scale.set(1, 1, 1);
    const box = Scenes.boundingBoxLocalMatrix(o2);
    if (box.isEmpty()) {
      box.set(v3(0, 0, 0), v3(0, 0, 0));
    }
    model.ModelData.boundingBoxUntransformed = box;
    (o2 as any).copyMatrix(this.getMatrix(model));
  }
}
